import React, {useState, useRef, useCallback} from 'react';
import Webcam from 'react-webcam';
import {Modal, Button, message} from 'antd';
import type {UploadRequestOption} from 'rc-upload/lib/interface';
import resources from "../../../resources";
import server from "../../../server";

const videoConstraints = {
    width: 1280,
    height: 720,
    facingMode: 'user',
};

interface WebcamCaptureProps {
    uploadFile: (opts: UploadRequestOption<any>) => void;
    reloadPreview: () => Promise<void>;
    hasProfilePhoto: boolean;
}

const tempResources = resources.commonButtonText;
const WebcamCapture: React.FC<WebcamCaptureProps> = ({uploadFile, reloadPreview, hasProfilePhoto,}) => {
    const webcamRef = useRef<Webcam>(null);
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [cameraModalVisible, setCameraModalVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [webcamKey, setWebcamKey] = useState(0);

    const capture = useCallback(() => {
        const image = webcamRef.current?.getScreenshot();
        if (image) setImageSrc(image);
    }, []);

    const loadImage = async () => {
        try {
            const params={purpose:'PROFILE_PHOTO',documentType:'PROFILE_PHOTO'};
            const resp = await server.document.find(params,0);
            if (resp.length === 0) return;
            const fileName = resp[0].fileName;
            const blob = await server.document.download(fileName);
            const imageURL = URL.createObjectURL(blob);
            setImageSrc(imageURL);
        } catch (err) {
            console.error("Failed to load image", err);
        }
    };

    const upload = async () => {
        if (!imageSrc) return message.error('No image captured');
        setLoading(true);
        try {
            const response = await fetch(imageSrc);
            const blob = await response.blob();
            const file = new File([blob], 'profile_photo.jpg', {type: 'image/jpeg'});

            await new Promise((resolve, reject) => {
                uploadFile({
                    file,
                    onSuccess: () => resolve(null),
                    onError: reject,
                } as any);
            });
            message.success('Profile photo updated');
            await loadImage();
            setImageSrc(null);
            setCameraModalVisible(false);
            stopWebcam();
        } catch (err) {
            message.error('Upload failed');
        } finally {
            setLoading(false);
        }
    };

    const stopWebcam = () => {
        const video = webcamRef.current?.video; //(Vo screen jisme live camera dikhta hai)
        const stream = video?.srcObject as MediaStream | null;  //(camera se aane wala real-time feed)
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
        }
        if (video) {
            video.srcObject = null;
        }
    };

    const onCancelCameraModal = () => {
        setCameraModalVisible(false)
        setImageSrc(null);
        stopWebcam();
    }

    return (
        <>
            <Button
                onClick={() => {
                    setWebcamKey(k => k + 1);  //webcam ko dubara load karta hai
                    setCameraModalVisible(true);
                }}
            >
                Take Photo
            </Button>
            <Modal
                className={'hrms-app modal'}
                open={cameraModalVisible}
                footer={null}
                onCancel={onCancelCameraModal}
            >
                <div style={{textAlign: 'center'}}>
                    {imageSrc ? (
                        <>
                            <img src={imageSrc} alt="Captured" style={{maxWidth: '100%'}}/>
                            <div style={{marginTop: 16}}>
                                <Button onClick={() => setImageSrc(null)} style={{marginRight: 8}}>
                                    Retake
                                </Button>
                                <Button type="primary" loading={loading} onClick={upload}>
                                    {tempResources.uploadButton}
                                </Button>
                            </div>
                        </>
                    ) : (
                        <>
                            <Webcam
                                audio={false}
                                key={webcamKey}  // webcamKey is to restart camera
                                ref={webcamRef}
                                screenshotFormat="image/jpeg"
                                videoConstraints={videoConstraints}
                                style={{width: '100%', borderRadius: 4}}
                            />
                            <Button
                                style={{marginTop: 16}}
                                type="primary"
                                onClick={capture}
                            >
                                Capture
                            </Button>
                        </>
                    )}
                </div>
            </Modal>
        </>
    );
};

export default WebcamCapture;
